import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  InputLabel,
  Grid,
  Box,
  IconButton,
  Skeleton,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { toggleLoader } from "../../store/loader/loaderReducer";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import TimeZones from "../../utils/TimeZone";
import AppHeader from '../../component/appHeader/AppHeader';
import styles from './OrganiZation.module.css';

const OrganizationDetails = () => {
  const [formData, setFormData] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    country: "",
    zip_code: "",
    email: "",
    time_zone: "Asia/Kolkata",
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [organizationExists, setOrganizationExists] = useState(false);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const { OrgId } = useSelector((state) => state?.document?.value);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (OrgId) {
      setLoading(true);
      dispatch(toggleLoader(true));
      axios.get(process.env.REACT_APP_EMAIL_BRANDING, {
        withCredentials: process.env.REACT_APP_CREDENTIALS, params: { org_id: OrgId }
      })
        .then((res) => {
          dispatch(toggleLoader(false));
          setLoading(false);
          const responseData = res.data.response;
          setFormData({
            name: responseData?.Name || "",
            street: responseData?.Street || "",
            city: responseData?.City || "",
            state: responseData?.State || "",
            country: responseData?.Country || "",
            zip_code: responseData?.Zip_Code || "",
            email: responseData?.Email || "",
            time_zone: responseData?.Time_Zone || "Asia/Kolkata",
            file_id: responseData?.File_Id || null,
          });

          if (responseData?.Name) {
            setOrganizationExists(true);
          }

          if (responseData.File_Id) {
            const baseUrl = process.env.REACT_APP_EMAIL_BRANDING_IMAGE;
            const logoUrl = `${baseUrl}?file_id=${responseData.File_Id}`;
            setLogoPreviewUrl(logoUrl);
          }
        })
        .catch((err) => {
          dispatch(toggleLoader(false));
          toast.error(err.message);
          setOrganizationExists(false);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
    } else {
      setLogoFile(null);
      setLogoPreviewUrl(null);
    }
  };

  const handleDeleteLogo = () => {
    if (!formData?.file_id) {
      setLogoFile(null);
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
      setLogoPreviewUrl(null);
    } else {
      dispatch(toggleLoader(true));
      const data = { org_id: OrgId, file_id: formData?.file_id };
      axios.delete(process.env.REACT_APP_EMAIL_BRANDING, {
        withCredentials: process.env.REACT_APP_CREDENTIALS, data: data
      })
        .then((res) => {
          setLogoFile(null);
          if (logoPreviewUrl) {
            URL.revokeObjectURL(logoPreviewUrl);
          }
          setLogoPreviewUrl(null);
          let data = { ...formData };
          delete data.file_id;
          setFormData(data);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => {
          setLoading(false);
          dispatch(toggleLoader(false));
        });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(toggleLoader(true));
    let apiCall;

    const { file_id: _omitFileId, ...sanitizedFormData } = formData;

    if (organizationExists && logoFile) {
      const formDatas = new FormData();
      formDatas.append("org_id", OrgId);

      Object.keys(sanitizedFormData).forEach((key) => {
        formDatas.append(key, sanitizedFormData[key]);
      });

      formDatas.append('file', logoFile);

      apiCall = axios.post(
        process.env.REACT_APP_EMAIL_BRANDING,
        formDatas,
        { withCredentials: process.env.REACT_APP_CREDENTIALS }
      );
    }
    else if (organizationExists) {
      const jsonPayload = {
        org_id: OrgId,
        ...sanitizedFormData,
      };

      apiCall = axios.put(
        process.env.REACT_APP_EMAIL_BRANDING,
        jsonPayload,
        {
          withCredentials: process.env.REACT_APP_CREDENTIALS,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    }
    else {
      const formDatas = new FormData();
      formDatas.append("org_id", OrgId);

      Object.keys(sanitizedFormData).forEach((key) => {
        formDatas.append(key, sanitizedFormData[key]);
      });

      if (logoFile) {
        formDatas.append('file', logoFile);
      }

      apiCall = axios.post(
        process.env.REACT_APP_EMAIL_BRANDING,
        formDatas,
        { withCredentials: process.env.REACT_APP_CREDENTIALS }
      );
    }

    apiCall
      .then((res) => {
        dispatch(toggleLoader(false));
        toast.success("Organization details saved successfully!");
        setOrganizationExists(true);
        if (res.data.response?.File_Id) {
          const baseUrl = process.env.REACT_APP_EMAIL_BRANDING_IMAGE;
          const logoUrl = `${baseUrl}?file_id=${res.data.response.File_Id}`;
          setLogoPreviewUrl(logoUrl);
        }
      })
      .catch((err) => {
        dispatch(toggleLoader(false));
        toast.error(err.message);
      });
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    gap: "16px",
    width: "100%",
    minWidth: "500px",
  };

  const labelStyle = {
    width: "150px",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: "var(--primary-font-family)",
    color: "var(--table-font-color)",
  };

  const timeZoneOptions = Object.entries(TimeZones).map(([key, value]) => ({
    key,
    ...value,
  }));

  return (
    <Box className={styles.container}>
      <AppHeader
        moduleText='Organization details'
        rolePermissions={true}
        checkAdmin={true}
      />

      <form onSubmit={handleSubmit} className={styles.formContainer}>
        {loading ? (
          <Box>
            <Skeleton variant="rectangular" height={150} style={{ marginBottom: "12px", borderRadius: "12px" }} />
            <Skeleton variant="rectangular" height={44} style={{ marginBottom: "10px" }} />
            <Skeleton variant="rectangular" height={44} style={{ marginBottom: "10px" }} />
            <Skeleton variant="rectangular" height={44} style={{ marginBottom: "10px" }} />
            <Skeleton variant="rectangular" height={44} style={{ marginBottom: "10px" }} />
            <Skeleton variant="rectangular" height={44} style={{ marginBottom: "10px" }} />
            <Skeleton variant="rectangular" height={44} style={{ marginBottom: "10px" }} />
          </Box>
        ) : (
          <Box>
            {/* Logo Section */}
            <Box className={styles.logoSection}>
              <Typography className={styles.logoLabel}>Organization Logo</Typography>
              <Box className={styles.logoUploadBox}>
                {logoPreviewUrl ? (
                  <Box className={styles.logoPreview}>
                    <img
                      src={logoPreviewUrl}
                      alt="Organization Logo"
                      className={styles.logoImage}
                    />
                    <Typography className={styles.orgName}>
                      {formData.name || "Your Organization Name"}
                    </Typography>
                    <Typography className={styles.orgTagline}>
                      Scale your business faster
                    </Typography>
                    <IconButton
                      onClick={handleDeleteLogo}
                      className={styles.deleteButton}
                      size="small"
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" gutterBottom sx={{ color: '#64748b', mb: 1 }}>
                      Upload Logo
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      sx={{
                        textTransform: "capitalize",
                        borderRadius: "8px",
                        borderColor: "var(--border-color)"
                      }}
                    >
                      Choose File
                      <input type="file" onChange={handleFileChange} hidden />
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Name Field */}
            <Box className={styles.formField}>
              <Typography className={styles.fieldLabel}>Name</Typography>
              <TextField
                name="name"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                placeholder="Organization Name"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "var(--border-color)" },
                    "&:hover fieldset": { borderColor: "var(--primary)" },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--primary)",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "12px 16px !important",
                    fontSize: "14px",
                    fontFamily: "var(--primary-font-family)",
                  },
                }}
              />
            </Box>

            {/* Street Field */}
            <Box className={styles.formField}>
              <Typography className={styles.fieldLabel}>Street</Typography>
              <TextField
                name="street"
                fullWidth
                value={formData.street}
                onChange={handleChange}
                placeholder="123 Business St"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "var(--border-color)" },
                    "&:hover fieldset": { borderColor: "var(--primary)" },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--primary)",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "12px 16px !important",
                    fontSize: "14px",
                    fontFamily: "var(--primary-font-family)",
                  },
                }}
              />
            </Box>

            {/* City and State Row */}
            <Box className={styles.gridRow}>
              <Box>
                <Typography className={styles.fieldLabel}>City</Typography>
                <TextField
                  name="city"
                  fullWidth
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "var(--border-color)" },
                      "&:hover fieldset": { borderColor: "var(--primary)" },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--primary)",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "14px 16px !important",
                      fontSize: "14px",
                      fontFamily: "var(--primary-font-family)",
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography className={styles.fieldLabel}>State</Typography>
                <TextField
                  name="state"
                  fullWidth
                  value={formData.state}
                  onChange={handleChange}
                  placeholder="State"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "var(--border-color)" },
                      "&:hover fieldset": { borderColor: "var(--primary)" },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--primary)",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "14px 16px !important",
                      fontSize: "14px",
                      fontFamily: "var(--primary-font-family)",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Country and Zip Row */}
            <Box className={styles.gridRow}>
              <Box>
                <Typography className={styles.fieldLabel}>Country</Typography>
                <TextField
                  name="country"
                  fullWidth
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="Country"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "var(--border-color)" },
                      "&:hover fieldset": { borderColor: "var(--primary)" },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--primary)",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "14px 16px !important",
                      fontSize: "14px",
                      fontFamily: "var(--primary-font-family)",
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography className={styles.fieldLabel}>Zip/Postal code</Typography>
                <TextField
                  name="zip_code"
                  fullWidth
                  value={formData.zip_code}
                  onChange={handleChange}
                  placeholder="12345"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": { borderColor: "var(--border-color)" },
                      "&:hover fieldset": { borderColor: "var(--primary)" },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--primary)",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "14px 16px !important",
                      fontSize: "14px",
                      fontFamily: "var(--primary-font-family)",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Email Field */}
            <Box className={styles.formField}>
              <Typography className={styles.fieldLabel}>Email</Typography>
              <TextField
                name="email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                placeholder="org@company.com"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "var(--border-color)" },
                    "&:hover fieldset": { borderColor: "var(--primary)" },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--primary)",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "12px 16px !important",
                    fontSize: "14px",
                    fontFamily: "var(--primary-font-family)",
                  },
                }}
              />
            </Box>

            {/* Time Zone Field */}
            <Box className={styles.formField} >
              <Typography className={styles.fieldLabel}>Time zone</Typography>
              <Autocomplete
                fullWidth
                options={timeZoneOptions}
                getOptionLabel={(option) => option.display_text || ""}
                value={
                  timeZoneOptions.find((tz) => tz.key === formData.time_zone) || null
                }
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    time_zone: newValue ? newValue.key : "",
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select time zone"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontFamily: "var(--primary-font-family)",
                        fontSize: "14px",
                        "& fieldset": { borderColor: "var(--border-color)" },
                        "&:hover fieldset": { borderColor: "var(--primary)" },
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--primary)",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "8px 16px !important",
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        )}
        <Box className={styles.saveButtonContainer}>
          <Button
            type="submit"
            variant="contained"
            className={styles.saveButton}
          >
            Save changes
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default OrganizationDetails;